import { useEffect, useState } from "react";
import { Share, X, Download, Smartphone, Link2 } from "lucide-react";
import { toast } from "sonner";

const KEY = "equiro_pwa_dismissed_v1";

function detect() {
  const ua = navigator.userAgent || "";
  const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  const isSafari = /^((?!chrome|android|crios|fxios|edgios|edg).)*safari/i.test(ua);
  const isMobile = isIOS || isAndroid;
  return { standalone, isIOS, isAndroid, isSafari, isMobile };
}

export default function PwaInstallGuidance() {
  const [show, setShow] = useState(false);
  const [env, setEnv] = useState({});
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    const e = detect();
    setEnv(e);
    if (e.standalone || !e.isMobile || localStorage.getItem(KEY) === "1") return;
    const onPrompt = (ev) => { ev.preventDefault(); setDeferred(ev); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const t = setTimeout(() => setShow(true), 1500);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(t); };
  }, []);

  const dismiss = () => { localStorage.setItem(KEY, "1"); setShow(false); };
  const copyLink = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      else { const t = document.createElement("textarea"); t.value = url; document.body.appendChild(t); t.select(); document.execCommand("copy"); document.body.removeChild(t); }
      toast.success("Link copiato. Aprilo in Safari per aggiungere Equiro alla schermata Home.");
    } catch { toast.error("Copia non riuscita. Copia manualmente l'indirizzo dalla barra."); }
  };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* noop */ }
    dismiss();
  };
  if (!show) return null;

  return (
    <div data-testid="pwa-guidance" className="fixed inset-x-3 bottom-3 z-[9999] sm:left-auto sm:right-4 sm:w-96">
      <div className="rounded-2xl border border-[#C9A227]/40 bg-[#152E22] shadow-2xl p-4 text-[#EAE4D3]">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-[#C9A227]/20 flex items-center justify-center shrink-0"><Smartphone className="h-5 w-5 text-[#C9A227]" /></div>
          <div className="min-w-0 flex-1">
            <p className="font-display font-semibold">Usa Equiro come un'app</p>
            {env.isIOS ? (
              env.isSafari ? (
                <p className="text-xs text-[#8FA396] mt-1" data-testid="pwa-ios-safari">Tocca <Share className="inline h-3.5 w-3.5 mx-0.5" />Condividi in basso, poi «Aggiungi alla schermata Home».</p>
              ) : (
                <p className="text-xs text-[#8FA396] mt-1" data-testid="pwa-ios-nonsafari">Per aggiungere Equiro alla schermata Home, aprilo in <b>Safari</b> e usa Condividi → «Aggiungi alla schermata Home».</p>
              )
            ) : (
              deferred ? (
                <p className="text-xs text-[#8FA396] mt-1" data-testid="pwa-android-installable">Installa Equiro per aprirlo come una normale app dalla schermata Home.</p>
              ) : (
                <p className="text-xs text-[#8FA396] mt-1" data-testid="pwa-android-fallback">Apri il menu del browser (⋮) e scegli «Installa app» o «Aggiungi alla schermata Home».</p>
              )
            )}
            <div className="flex gap-2 mt-3">
              {env.isAndroid && deferred && (
                <button data-testid="pwa-install-btn" onClick={install} className="inline-flex items-center gap-1.5 rounded-lg bg-[#C9A227] text-[#0F241A] text-xs font-semibold px-3 py-1.5">
                  <Download className="h-3.5 w-3.5" /> Installa Equiro
                </button>
              )}
              {env.isIOS && (
                <button data-testid="pwa-copy-link-btn" onClick={copyLink} className="inline-flex items-center gap-1.5 rounded-lg border border-[#C9A227] text-[#C9A227] text-xs font-semibold px-3 py-1.5">
                  <Link2 className="h-3.5 w-3.5" /> Copia link
                </button>
              )}
              <button data-testid="pwa-dismiss-btn" onClick={dismiss} className="rounded-lg border border-[#25402F] text-[#EAE4D3] text-xs px-3 py-1.5">
                {env.isAndroid && deferred ? "Non ora" : "Ho capito"}
              </button>
            </div>
          </div>
          <button aria-label="Chiudi" data-testid="pwa-close" onClick={dismiss} className="text-[#8FA396] hover:text-[#EAE4D3] shrink-0"><X className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}
